import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { useRecordDocument } from '../components/RecordDocument.tsx';
import { formatDate } from '../dates.ts';
import {
  useClosedByRule, useConfirmRows, useNotRight, useReviewRows, useReviewSummary,
  type ReviewKind, type ReviewRow,
} from '../api/readingReview.ts';
import {
  CLOSED_BY_RULE, CONFIRM, CONFIRMABLE, CONFIRM_ONE, CONFIRM_SELECTED, GO_BACK, NOT_RIGHT, NOT_RIGHT_DONE,
  NOT_RIGHT_QUESTION, NOT_RIGHT_REASONS, OPEN_FIRST, PAGE_LINE, PAGE_TITLE, RECORDED, RECORD_KINDS, SELECT_ALL,
  SELECT_ROW, STATUS_HREF, STATUS_LINK, TAB_COUNT, confirmSummary, confirmedSingly, hasBox, inSelectAll, kindStatus,
  notRecorded, notYetOpen, proofPaper, proofRecord, resultTotal, rowKey, rowsToSend, stateCount, strikeLine, yardstick,
  type KindCode,
} from '../foundInDocuments.ts';

const button = 'inline-flex min-h-[44px] items-center rounded-lg border border-neutral-300 bg-panel px-4 text-sm font-semibold text-neutral-900 hover:bg-neutral-50';
const primary = 'inline-flex min-h-[44px] items-center rounded-lg bg-brand px-4 text-sm font-semibold text-on-brand hover:bg-accent';

/**
 * Section R (176): "Found in the documents". One tab per kind; a row offers its proof and, for a paper, the document
 * through P-20's own audited preview; up to 50 rows are confirmed at once, each in its own transaction, with a result
 * per row; "Not right" records why and closes nothing. Each kind that may close by rule shows one read-only status
 * line linking to Company policies, where the toggle is (A-#608 5, components/ClosingByRule.tsx). Who may see the
 * page, and every refusal, is the server's.
 */
export function FoundInDocuments() {
  const summary = useReviewSummary();
  const [tab, setTab] = useState<KindCode | 'CLOSED'>('R_OATH');
  const [closedKind, setClosedKind] = useState<KindCode>('R_OATH');
  const kinds = (summary.data?.kinds ?? []).filter((k): k is ReviewKind & { code: KindCode } => k.code !== 'DATE_DIFFERS');
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">{PAGE_TITLE}</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">{PAGE_LINE}</p>
        </header>
        <div className="page-body">
          {summary.error && !summary.data ? (
            <LoadFailure what={PAGE_TITLE} error={summary.error} onRetry={() => void summary.refetch()} retrying={summary.isFetching} />
          ) : !summary.data ? (
            <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>
          ) : (
            <>
              <nav aria-label="Kinds" className="flex flex-wrap gap-2 border-b border-neutral-200 px-4 py-2 lg:px-5">
                {kinds.map((k) => (
                  <button key={k.code} type="button" aria-pressed={tab === k.code} onClick={() => setTab(k.code)}
                          className={`${button} ${tab === k.code ? 'border-brand' : ''}`}>{k.label}</button>
                ))}
                <button type="button" aria-pressed={tab === 'CLOSED'} onClick={() => setTab('CLOSED')}
                        className={`${button} ${tab === 'CLOSED' ? 'border-brand' : ''}`}>{CLOSED_BY_RULE}</button>
              </nav>
              {tab === 'CLOSED' ? (
                <ClosedByRule kind={closedKind} onKind={setClosedKind} kinds={kinds.filter((k) => RECORD_KINDS.includes(k.code))} />
              ) : (
                <>
                  <KindStatus kind={kinds.find((k) => k.code === tab)} />
                  <KindTab kind={tab} label={kinds.find((k) => k.code === tab)?.label ?? tab} />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KindTab({ kind, label }: { kind: KindCode; label: string }) {
  const rows = useReviewRows(kind);
  const summary = useReviewSummary();
  const confirm = useConfirmRows();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [asking, setAsking] = useState(false);
  const list = rows.data?.rows ?? [];
  const page = list.slice(0, 50);
  const confirmable = CONFIRMABLE.includes(kind);
  const keyOf = rowKey;
  const toggle = (k: string) => setPicked((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const chosen = rowsToSend(page, picked);   // what Confirm sends, and the number the question names (L2)
  const all = page.filter(inSelectAll);      // 180: "select all" never takes a row the strike pass did not clear
  const yard = kind === 'P_COMMISSION' ? summary.data?.yardstick.P_COMMISSION ?? null : null;
  const send = () => {
    confirm.mutate({ kind, rows: chosen }, { onSettled: () => { setAsking(false); setPicked(new Set()); } });
  };
  if (rows.error && !rows.data) {
    const notOpen = notYetOpen(rows.error);   // W2: the server's own words, as a state, never "could not be loaded"
    if (notOpen) return <p role="status" className="px-4 py-6 text-sm text-neutral-700 lg:px-5">{notOpen}</p>;
    return <LoadFailure what={label} error={rows.error} onRetry={() => void rows.refetch()} retrying={rows.isFetching} />;
  }
  if (!rows.data) return <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>;
  return (
    <section aria-label={label} className="px-4 py-3 lg:px-5">
      <p className="text-sm text-neutral-700">{TAB_COUNT(list.length)}</p>
      {kind === 'P_COMMISSION' ? (
        <p className="text-sm text-neutral-700">{yard ? `${yardstick(yard.agrees, yard.with_paper)} · ` : ''}{stateCount(list)}</p>
      ) : null}
      {confirm.data ? (
        <div role="status" className="my-2 rounded-lg border border-neutral-200 bg-panel p-3 text-sm">
          <p className="font-semibold">{resultTotal(confirm.data.recorded, confirm.data.not_recorded)}</p>
          <ul>{confirm.data.results.filter((r) => !r.recorded).map((r) => <li key={r.deadline_id}>{notRecorded(r.why ?? '')}</li>)}</ul>
        </div>
      ) : null}
      {confirm.error ? <p role="alert" className="my-2 text-sm text-critical-edge">{confirm.error.message}</p> : null}
      {confirmable && page.length > 0 ? (
        <div className="my-2 flex flex-wrap items-center gap-3">
          <label className="inline-flex min-h-[44px] items-center gap-2 text-sm">
            <input type="checkbox" className="h-5 w-5" checked={all.length > 0 && all.every((r) => picked.has(keyOf(r)))}
                   onChange={(e) => setPicked(e.target.checked ? new Set(all.map(keyOf)) : new Set())} />
            {SELECT_ALL}
          </label>
          {asking ? (
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm">{confirmSummary(chosen.length, label.toLowerCase())}</span>
              <button type="button" className={primary} disabled={confirm.isPending} onClick={send}>{CONFIRM}</button>
              <button type="button" className={button} onClick={() => setAsking(false)}>{GO_BACK}</button>
            </span>
          ) : (
            <button type="button" className={primary} disabled={chosen.length === 0} onClick={() => setAsking(true)}>{CONFIRM_SELECTED}</button>
          )}
        </div>
      ) : null}
      <ul className="divide-y divide-neutral-100">
        {page.map((r) => (
          <Row key={keyOf(r)} kind={kind} row={r} confirmable={confirmable} picked={picked.has(keyOf(r))} onPick={() => toggle(keyOf(r))} />
        ))}
      </ul>
    </section>
  );
}

function Row({ kind, row, confirmable, picked, onPick }: { kind: KindCode; row: ReviewRow; confirmable: boolean; picked: boolean; onPick: () => void }) {
  const clientId = row.client?.id ?? '';
  const doc = useRecordDocument(clientId, row.clio_copy_id
    ? { upload_id: null, upload_name: null, upload_status: null, clio_copy_id: row.clio_copy_id } : null, 'paper');
  const [why, setWhy] = useState<string>('');
  const [said, setSaid] = useState(false);
  const notRight = useNotRight();
  const one = useConfirmRows();   // 180: a page not checked for a line is confirmed on its own, beside the page
  const boxed = confirmable && hasBox(row);
  const singly = confirmable && confirmedSingly(row);
  const oneResult = one.data?.results[0];
  const name = row.client ? `${row.client.last_name}${row.client.first_name ? `, ${row.client.first_name}` : ''}` : '';
  const proof = RECORD_KINDS.includes(kind)
    ? proofRecord(kind, row.on_date ? formatDate(row.on_date) : '')
    : kind === 'P_OATH' ? `${row.proposed ? formatDate(row.proposed) : ''} · ${proofPaper(row.page_no ?? null, 'oath and designation')}`
    : kind === 'P_COMMISSION' ? `${row.proposed ? formatDate(row.proposed) : ''} · ${proofPaper(row.page_no ?? null, 'commission')}`
    : kind === 'OSC_DATE' ? `Read ${row.read_date ? formatDate(row.read_date) : ''}; on file ${row.on_file ? formatDate(row.on_file) : 'none'}`
    : kind === 'ORDER' ? `Index ${row.court_index_number ?? ''}: no order on file with it` : 'A discharge order is in the papers';
  return (
    <li className="flex flex-wrap items-start gap-3 py-2" data-quick-client={clientId}>
      {boxed ? (
        <input type="checkbox" aria-label={SELECT_ROW} className="mt-3 h-5 w-5" checked={picked} onChange={onPick} />
      ) : null}
      <div className="min-w-0 flex-1">
        {row.strike ? (
          <span className={`block text-sm ${row.strike === 'clear' ? 'text-neutral-700' : 'font-semibold text-neutral-900'}`}>
            {strikeLine(row.strike, row.unchecked_why)}
          </span>
        ) : null}
        <Link to={`/clients/${clientId}/legal`} state={{ from: PAGE_TITLE }}
              className="inline-flex min-h-[44px] items-center text-neutral-900 underline-offset-2 hover:underline">{name}</Link>
        <span className="block text-sm text-neutral-500">
          {row.client?.client_ref}{row.court_order ? ` · ${row.court_order.type ?? ''}${row.court_order.signed_date ? `, signed ${formatDate(row.court_order.signed_date)}` : ''}` : ''}
        </span>
        <span className="block text-sm text-neutral-800">{proof}</span>
      </div>
      {row.clio_copy_id ? doc.control : null}
      {doc.preview}
      {singly && row.deadline_id ? (
        oneResult ? (
          <span role="status" className="text-sm text-neutral-700">{oneResult.recorded ? RECORDED : notRecorded(oneResult.why ?? '')}</span>
        ) : (
          <span className="flex flex-wrap items-center gap-2">
            <button type="button" className={primary} disabled={!doc.opened || one.isPending}
                    onClick={() => one.mutate({ kind, rows: [{ deadline_id: row.deadline_id!, field_id: row.field_id ?? null }] })}>
              {CONFIRM_ONE}
            </button>
            {!doc.opened ? <span className="text-sm text-neutral-700">{OPEN_FIRST}</span> : null}
          </span>
        )
      ) : null}
      {one.error ? <p role="alert" className="w-full text-sm text-critical-edge">{one.error.message}</p> : null}
      {row.deadline_id && !said ? (
        <span className="flex flex-wrap items-center gap-2">
          <label className="text-sm">{NOT_RIGHT_QUESTION}
            <select className="ml-2 min-h-[44px] rounded border border-neutral-300 bg-panel" value={why} onChange={(e) => setWhy(e.target.value)}>
              <option value="">—</option>
              {NOT_RIGHT_REASONS.map((r) => <option key={r.code} value={r.code}>{r.words}</option>)}
            </select>
          </label>
          <button type="button" className={button} disabled={!why || notRight.isPending}
                  onClick={() => notRight.mutate({ kind, deadline_id: row.deadline_id!, field_id: row.field_id ?? null, why },
                    { onSuccess: () => setSaid(true) })}>{NOT_RIGHT}</button>
        </span>
      ) : said ? <span className="text-sm text-neutral-700">{NOT_RIGHT_DONE}</span> : null}
      {notRight.error ? <p role="alert" className="w-full text-sm text-critical-edge">{notRight.error.message}</p> : null}
    </li>
  );
}

function ClosedByRule({ kind, onKind, kinds }: { kind: KindCode; onKind: (k: KindCode) => void; kinds: ReviewKind[] }) {
  const rows = useClosedByRule(kind);
  return (
    <section aria-label={CLOSED_BY_RULE} className="px-4 py-3 lg:px-5">
      <div className="flex flex-wrap gap-2">
        {kinds.map((k) => (
          <button key={k.code} type="button" aria-pressed={kind === k.code} onClick={() => onKind(k.code as KindCode)}
                  className={`${button} ${kind === k.code ? 'border-brand' : ''}`}>{k.label}</button>
        ))}
      </div>
      {rows.error && !rows.data ? (
        <LoadFailure what={CLOSED_BY_RULE} error={rows.error} onRetry={() => void rows.refetch()} retrying={rows.isFetching} />
      ) : !rows.data ? <p className="py-6 text-neutral-600">Loading…</p> : (
        <ul className="divide-y divide-neutral-100">
          {rows.data.rows.map((r) => (
            <li key={r.deadline_id} className="py-2 text-sm">
              <Link to={`/clients/${r.client?.id ?? ''}/legal`} className="inline-flex min-h-[44px] items-center underline-offset-2 hover:underline">
                {r.client ? `${r.client.last_name}${r.client.first_name ? `, ${r.client.first_name}` : ''}` : ''}
              </Link>
              <span className="block text-neutral-500">{r.client?.client_ref} · {RECORDED} {formatDate(r.completed_on)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** A-#608 5: one read-only line per kind that may close by rule, linking to Company policies, where the toggle is. */
function KindStatus({ kind }: { kind: ReviewKind | undefined }) {
  if (!kind || !kind.may_auto) return null;
  const words = kindStatus(kind);
  return (
    <p className="px-4 pt-3 text-sm text-neutral-700 lg:px-5">
      {words} · <Link to={STATUS_HREF} className="inline-flex min-h-[44px] items-center underline underline-offset-2">{STATUS_LINK}</Link>
    </p>
  );
}
